import React from 'react';
import { Copy, Pencil, Trash2 } from 'lucide-react';
import { BuilderChip } from '@/components/BuilderChip';
import {
  DetailSheet,
  DetailSheetActions,
  DetailSheetCol,
  DetailSheetEmpty,
  DetailSheetHead,
  DetailSheetLink,
  DetailSheetRow,
  DetailSheetSection,
  DetailSheetChips,
  DetailSheetText,
  type DetailSheetActionItem,
} from '@/components/DetailSheet';
import { CompetencyChips } from '@/components/CompetencyChips';
import { formatDate } from '@/utils/formatters';
import type { QuestionSetWithPackages } from '@/services/api';

interface QuestionSetDetailSheetProps {
  questionSet: QuestionSetWithPackages;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onNavigateToPackage: (id: string) => void;
}

export const QuestionSetDetailSheet: React.FC<QuestionSetDetailSheetProps> = ({
  questionSet,
  onEdit,
  onDuplicate,
  onDelete,
  onNavigateToPackage,
}) => {
  const questionCount = questionSet.items.length;
  const packageCount = questionSet.packages.length;
  const requiresHts = questionSet.htsQuestionnaireId !== null;

  const actions: DetailSheetActionItem[] = [
    {
      key: 'edit',
      label: 'Edit question set',
      icon: <Pencil style={{ width: '1em', height: '1em' }} />,
      onClick: onEdit,
    },
    {
      key: 'duplicate',
      label: 'Duplicate',
      icon: <Copy style={{ width: '1em', height: '1em' }} />,
      onClick: onDuplicate,
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <Trash2 style={{ width: '1em', height: '1em' }} />,
      danger: true,
      onClick: onDelete,
    },
  ];

  const competencies = Array.from(
    new Set(questionSet.items.map((item) => item.question.competencyId))
  );

  return (
    <DetailSheet>
      <DetailSheetHead
        title={questionSet.name}
        tags={
          <>
            {requiresHts && <BuilderChip tint="purple">HTS</BuilderChip>}
            <BuilderChip tint="periwinkle">
              {questionCount} question{questionCount !== 1 ? 's' : ''}
            </BuilderChip>
          </>
        }
      />

      {questionSet.description && (
        <DetailSheetSection label="Description">
          <DetailSheetText>{questionSet.description}</DetailSheetText>
        </DetailSheetSection>
      )}

      <DetailSheetRow>
        <DetailSheetCol label="Created" value={formatDate(questionSet.createdAt)} />
        <DetailSheetCol label="Updated" value={formatDate(questionSet.updatedAt)} />
      </DetailSheetRow>

      <DetailSheetSection label="Competencies covered">
        {competencies.length === 0 ? (
          <DetailSheetEmpty>No questions in this set yet</DetailSheetEmpty>
        ) : (
          <DetailSheetChips>
            <CompetencyChips competencyIds={competencies} />
          </DetailSheetChips>
        )}
      </DetailSheetSection>

      <DetailSheetSection label="Used in">
        {packageCount === 0 ? (
          <DetailSheetEmpty>Not used in any assessment package</DetailSheetEmpty>
        ) : (
          questionSet.packages.map((pkg) => (
            <DetailSheetLink key={pkg.id} name={pkg.name} onClick={() => onNavigateToPackage(pkg.id)} />
          ))
        )}
      </DetailSheetSection>

      <DetailSheetActions items={actions} />
    </DetailSheet>
  );
};

export default QuestionSetDetailSheet;
